import React, { HTMLAttributes, MouseEventHandler, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { useClickAwayListener } from './useClickAwayListener';

export interface DropdownMenuItem {
  href?: string;
  title?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  component?: React.ReactNode;
  icon?: React.ReactNode;
  itemClassName?: string;
}

export interface DropdownMenuProps extends HTMLAttributes<HTMLDivElement> {
  items: DropdownMenuItem[];
  className?: string;
  triggerClassName?: string;
  itemsClassName?: string;
  icon?: React.ReactNode;
}

export const DropdownMenu = ({
  items,
  className,
  icon,
  triggerClassName,
  itemsClassName,
  ...rest
}: DropdownMenuProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLButtonElement>(null);

  useClickAwayListener(ref, () => {
    if (open) {
      menuRef.current?.focus();
    }
    setOpen(false);
  });

  const onItemClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    onClick?: MouseEventHandler<HTMLButtonElement>,
  ) => {
    onClick?.(event);
    setOpen(false);
  };

  const toggleDropdown = () => {
    setOpen((currentValue) => !currentValue);
    if (open) {
      menuRef.current?.focus();
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        menuRef.current?.focus();
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div {...rest} ref={ref} className={twMerge('relative inline-block text-left', className)}>
      <button
        id="menu-button"
        type="button"
        data-testid="dropdown-menu"
        onClick={toggleDropdown}
        className={twMerge(
          'inline-flex w-full justify-center gap-x-1.5 px-3 py-2 text-sm font-semibold text-gray-900 hover:rounded-md hover:bg-gray-50',
          triggerClassName,
        )}
        aria-expanded={open ? 'true' : 'false'}
        ref={menuRef}
      >
        {icon || (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.99998 5.83333C10.9166 5.83333 11.6666 5.08333 11.6666 4.16667C11.6666 3.25 10.9166 2.5 9.99998 2.5C9.08331 2.5 8.33331 3.25 8.33331 4.16667C8.33331 5.08333 9.08331 5.83333 9.99998 5.83333ZM9.99998 8.33333C9.08331 8.33333 8.33331 9.08333 8.33331 10C8.33331 10.9167 9.08331 11.6667 9.99998 11.6667C10.9166 11.6667 11.6666 10.9167 11.6666 10C11.6666 9.08333 10.9166 8.33333 9.99998 8.33333ZM8.33331 15.8333C8.33331 14.9167 9.08331 14.1667 9.99998 14.1667C10.9166 14.1667 11.6666 14.9167 11.6666 15.8333C11.6666 16.75 10.9166 17.5 9.99998 17.5C9.08331 17.5 8.33331 16.75 8.33331 15.8333Z"
              fill="#374154ff"
            />
          </svg>
        )}
        <span className="sr-only">Open Menu</span>
      </button>

      <ul
        className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
        aria-labelledby="menu-button"
        tabIndex={-1}
        hidden={!open}
      >
        {items?.map(({ href, title, onClick, component, itemClassName, icon: itemIcon }, index) => (
          <li key={title}>
            {href ? (
              <Link
                to={href}
                className="flex w-full items-center gap-4 px-4 py-4 text-sm text-gray-700 hover:bg-[#E6E6E6]"
              >
                {itemIcon} {title}
              </Link>
            ) : (
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  onItemClick(event, onClick);
                  menuRef.current?.focus();
                }}
                className={twMerge(
                  'flex w-full cursor-pointer items-center gap-4 border-t-0 text-sm text-gray-700 hover:bg-[#E6E6E6]',
                  items.length - 1 !== index ? 'border-b border-b-[#E6E6E6]' : '',
                  'px-4 py-4',
                  itemsClassName,
                  itemClassName,
                )}
                id={`menu-item-${index}`}
              >
                {component ? (
                  component
                ) : (
                  <>
                    {itemIcon} {title}
                  </>
                )}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
